<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Customizations';
    let nextLink= '/Documentation/Customizations/External_Components';
    
    import Note from "$lib/notes/Note.svelte";
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="editor-customization-1"
  bind:intersecting={visibleById["editor-customization-1"]}
  onIntersectChange={(visible) => setVisible("editor-customization-1", visible)}
>Editor Customization</SectionComponent>
<p>You can customize the editor by adding projections, actions, and/or external Svelte components.
Before you dive into all this, we suggest that you become familiar with the Box Model,
as explained in the <a
  href="/Documentation/Under_the_Hood/Editor_Framework"
>Freon Editor Framework</a>.</p>
<SectionComponent tag="h2" 
  id="generated-box-providers-2"
  bind:intersecting={visibleById["generated-box-providers-2"]}
  onIntersectChange={(visible) => setVisible("generated-box-providers-2", visible)}
>Generated Box Providers</SectionComponent>
<p>The generated editor is implemented by a set of <strong>box providers</strong>. Every box provider is capable of returning the
box for a specific type of AST node. For instance, the generated box provider for the <code>EuroLiteral</code>
concept contains the following two methods:</p>
<pre class="language-ts"><code class="language-ts">// Insurance/src/freon/editor/gen/EuroLiteralBoxProvider.ts#L23-L62

</code></pre>
<p>The first method shows that any custom projection is always handled first.
The second method returns a <code>Box</code> based on the current content of <code>this._node</code>. Note that the
implementation relies heavily on the <a
  href="/Documentation/Under_the_Hood/Editor_Framework/Predefined_Boxes"
>predefined box types</a>, as well as on the predefined
helper <code>createDefaultExpressionBox</code>, which returns the correct box for a (non-binary) expression concept.</p>
<SectionComponent tag="h2" 
  id="writing-a-custom-projection-3"
  bind:intersecting={visibleById["writing-a-custom-projection-3"]}
  onIntersectChange={(visible) => setVisible("writing-a-custom-projection-3", visible)}
>Writing a Custom Projection</SectionComponent>
<p>Writing a custom projection does not require the implementation of a complete box provider. Instead,
a custom projection (set) is a series of methods, each returning a box object for an AST node.
Every custom method needs to be registered in <code>nodeTypeToBoxMethod</code>.</p>
<p>Custom table projections are not yet supported.</p>
<p>In the example below, we copy the <code>getDefault</code> method and adjust it to display an SVG Euro symbol instead of the
string ‘EUR’. Note that the method checks whether it is used for concepts of type <code>EuroLiteral</code>.</p>
<pre class="language-ts"><code class="language-ts">// Insurance/src/freon/editor/CustomInsuranceModelProjection.ts#L24-L85

    // add your custom methods here

    // BOX_FOR_CONCEPT(node: NAME_OF_CONCEPT) : Box &#123; ... &#125;

    // TABLE_DEFINITION_FOR_CONCEPT() : FreTableDefinition &#123; ... &#125;
&#125;
</code></pre>
<Note> {#snippet header()}Use another filename and/or location{/snippet}
{#snippet content()}
You can rename the file <code>~/freon/editor/CustomYourLanguageNameProjection.ts</code>, and/or place it in another location.
In that case, you need to adjust the file <code>~/freon/config/FreonConfiguration</code>. 
See <a href="/Documentation/Customizations#customizability-of-the-fre-tools-3">Adding TypeScript Files</a>.
{/snippet}
</Note>
<SectionComponent tag="h2" 
  id="writing-custom-actions-4"
  bind:intersecting={visibleById["writing-custom-actions-4"]}
  onIntersectChange={(visible) => setVisible("writing-custom-actions-4", visible)}
>Writing Custom Actions</SectionComponent>
<p>When you have created your own custom projection, this projection is exactly that:
a projection. Other than the built-in default behavior, there are no actions defined yet.
Actions are necessary to enable the user to change the model and add elements to it.
The projections only describe what you will see in the editor.
Actions determine what you can do, how you interact with the editor.</p>
<p>As a convenience, the file <code>~/freon/editor/CustomYourLanguageNameActions.ts</code> is
generated, which is a placeholder for your own actions written in TypeScript. This
file defines two constants for different kinds of actions. The constant
<code>MANUAL_CUSTOM_ACTIONS</code> is an array that contains all actions to be performed
on normal concepts. Actions on binary expressions should be added to the array
<code>MANUAL_BINARY_EXPRESSION_ACTIONS</code>. When you define these constants, Freon
merges your actions with the default actions.</p>
<p>Any <code>CustomAction</code> must implement the <a
  href="/Documentation/Under_the_Hood/Core_Interfaces#frecustomaction-5"
><code>FreCustomAction</code></a>
interface. An easy way to create one is to use the predefined method <code>FreCustomAction.create()</code>, which
takes a partial <code>CustomAction</code> object as a parameter. The most important properties of the <code>CustomAction</code>
object are the action itself, and the box roles that indicate the action could be activated.</p>
<p>In the example below two actions are added, one for the button that was added to the <code>BaseProduct</code> projection in
<a
  href="/Documentation/Defining_an_Editor/Buttons#adding-buttons-1"
>Buttons</a>, and one for the buttons that were added
to the table projection
of <code>InsurancePart</code> concepts in <a
  href="/Documentation/Defining_an_Editor/Buttons#icon-buttons-2"
>Icon Buttons</a>.
The first was defined with the box role “MyButton-role”, the second with “MyTableButton-role”. In this
example, the action implementation is a simple alert shown to the user.</p>
<pre class="language-ts"><code class="language-ts">// Insurance/src/custom/editor/CustomInsuranceModelActions.ts#L19-L82

export class CustomInsuranceModelActions implements FreCombinedActions &#123;
    binaryExpressionActions: FreCreateBinaryExpressionAction[] = MANUAL_BINARY_EXPRESSION_ACTIONS;
    customActions: FreCustomAction[] = MANUAL_CUSTOM_ACTIONS;
&#125;

export const MANUAL_BINARY_EXPRESSION_ACTIONS: FreCreateBinaryExpressionAction[] = [
    // Add your own custom binary expression actions here
];

export const MANUAL_CUSTOM_ACTIONS: FreCustomAction[] = [
    // Add your own custom behavior here
    FreCustomAction.create(&#123;
        activeInBoxRoles: ["MyButton-role"],
        action: (box: Box, trigger: FreTriggerType, ed: FreEditor): FreNode | null =&gt; &#123;
            // do something
            const thisNode: FreNode = box.node;
            // const thisParent: FreNode = box.element.freOwner();
            alert("You shouldn't have pushed the button with role 'MyButton-role' on element " + thisNode.freId() + ".\nPunishment awaits !!!!!!!!!!");
            return null;
        &#125;,
    &#125;),
    FreCustomAction.create(&#123;
        activeInBoxRoles: ["MyTableButton-role"],
        action: (box: Box, trigger: FreTriggerType, ed: FreEditor): FreNode | null =&gt; &#123;
            // do something
            const thisNode: FreNode = box.node;
            // const thisParent: FreNode = box.element.freOwner();
            alert("You shouldn't have pushed the button with role 'MyTableButton-role' on element " + thisNode.freId() + ".\nPunishment awaits !!!!!!!!!!");
            return null;
        &#125;,
    &#125;),
];
</code></pre>


<PrevNextSection {prevLink} {nextLink} />